import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import ActivityLogger from '../utils/activityLogger';
import NomenclatureTest from './NomenclatureTest';
import MonitoringDashboard from './MonitoringDashboard';
import CGTTemplatesManager from './CGTTemplatesManager';
import EmailTemplatesManager from './EmailTemplatesManager';
import LoginBrandingManager from './LoginBrandingManager';

const SettingsManager = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('templates-email');
  const [activeEmailSubTab, setActiveEmailSubTab] = useState('smtp');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // SMTP Configuration state
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_server: 'smtp.mail.ovh.net',
    smtp_port: 465,
    smtp_username: 'Dev-crm@baobaco.tech',
    smtp_password: '',
    security_type: 'SSL/TLS',
    sender_name: 'Baobab Connect',
    sender_email: 'Dev-crm@baobaco.tech'
  });
  
  // User signature state
  const [userSignature, setUserSignature] = useState({
    signature_text: `Cordialement,

{nom_utilisateur}
{fonction_utilisateur}
{email_utilisateur}
{telephone_utilisateur}

Baobab Connect
ERP / CRM Interne`
  });
  
  // Test email state
  const [testEmail, setTestEmail] = useState('');
  const [testLogs, setTestLogs] = useState([]);

  // LLM Configuration state
  const [llmConfigurations, setLlmConfigurations] = useState([]);
  const [selectedLlmProvider, setSelectedLlmProvider] = useState('openai');
  const [llmConfig, setLlmConfig] = useState({
    provider: 'openai',
    model_name: 'gpt-4',
    api_key: '',
    api_base_url: '',
    temperature: 0.7,
    max_tokens: 1000,
    is_active: true,
    is_default: false
  });
  const [testingLlm, setTestingLlm] = useState(false);
  const [llmTestResult, setLlmTestResult] = useState(null);

  // Roles and permissions state
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [permissionModules, setPermissionModules] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Modal states
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const [showInviteUserModal, setShowInviteUserModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    permissions: []
  });
  const [newInvitation, setNewInvitation] = useState({
    email: '',
    role_id: '',
    send_email: true
  });

  // Phase 2 - Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    in_app_notifications: true,
    notification_types: {
      user_tag: true,
      comment_reply: true,
      document_update: true,
      assignment: true,
      status_change: true,
      system: true
    }
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [currentLogo, setCurrentLogo] = useState(null);

  // Backup & Restore state
  const [backupInfo, setBackupInfo] = useState(null);
  const [backupGenerating, setBackupGenerating] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreOptions, setRestoreOptions] = useState({
    overwrite_existing: false,
    skip_users: false,
    skip_system_data: false
  });
  const [restoreResult, setRestoreResult] = useState(null);

  // Scheduled Backup state
  const [activeBackupTab, setActiveBackupTab] = useState('manual');
  const [scheduledBackupConfig, setScheduledBackupConfig] = useState({
    is_enabled: true,
    frequency: 'daily',
    hour: 2,
    minute: 0,
    day_of_week: null,
    day_of_month: null,
    compression_enabled: true,
    compression_format: 'gzip',
    retention_days: 30,
    max_backups: 10,
    collections_to_backup: [],
    notification_emails: []
  });
  const [backupHistory, setBackupHistory] = useState([]);
  const [backupStats, setBackupStats] = useState(null);
  const [backupHistoryLoading, setBackupHistoryLoading] = useState(false);

  // External Backup Servers state
  const [externalServers, setExternalServers] = useState([]);
  const [externalUploads, setExternalUploads] = useState([]);
  const [externalServersLoading, setExternalServersLoading] = useState(false);
  const [externalUploadsLoading, setExternalUploadsLoading] = useState(false);
  const [showServerForm, setShowServerForm] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [serverFormData, setServerFormData] = useState({
    name: '',
    protocol: 'ssh',
    host: '',
    port: 22,
    username: '',
    auth_type: 'password',
    password: '',
    ssh_private_key: '',
    ssh_key_passphrase: '',
    remote_directory: '/backups/baobab',
    is_enabled: true,
    auto_upload: true,
    keep_local_after_upload: true,
    max_retries: 3,
    retry_delay_seconds: 30
  });
  const [serverTestResult, setServerTestResult] = useState(null);
  const [testingServer, setTestingServer] = useState(null);

  // System Logs state
  const [systemLogs, setSystemLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFilters, setLogsFilters] = useState({
    user_id: '',
    action_type: '',
    document_type: '',
    start_date: '',
    end_date: '',
    is_internal: '',
    limit: 100,
    offset: 0
  });
  const [logsTotalCount, setLogsTotalCount] = useState(0);
  const [logsCurrentPage, setLogsCurrentPage] = useState(1);
  const [logsPerPage, setLogsPerPage] = useState(50);

  // Get backend URL from environment
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Get auth token with refresh capability
  const getAuthToken = () => {
    return localStorage.getItem('token');
  };

  // Enhanced API request helper with session management
  const apiRequest = async (endpoint, method = 'GET', data = null, isLongOperation = false) => {
    const token = getAuthToken();
    const config = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    };
    
    if (data) {
      config.body = JSON.stringify(data);
    }
    
    // For long operations, extend timeout
    const controller = new AbortController();
    config.signal = controller.signal;
    
    if (isLongOperation) {
      // Extend timeout for backup operations (5 minutes)
      setTimeout(() => controller.abort(), 300000);
    } else {
      // Standard timeout (30 seconds)
      setTimeout(() => controller.abort(), 30000);
    }
    
    try {
      const response = await fetch(`${backendUrl}/api/${endpoint}`, config);
      
      // Handle authentication errors with retry
      if (response.status === 401 || response.status === 403) {
        // Try to refresh authentication
        console.warn('Session may have expired, attempting to continue...');
        // For now, just show error - could implement token refresh here
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
        throw new Error(errorData.detail || `Erreur HTTP: ${response.status}`);
      }
      
      return response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Opération annulée - timeout dépassé. Veuillez réessayer.');
      }
      throw error;
    }
  };

  // Load SMTP configuration on component mount
  useEffect(() => {
    loadSmtpConfig();
    loadUserSignature();
    loadTestLogs();
    loadLlmConfigurations();
    loadRoles();
    loadPermissions();
    loadUsers();
    loadInvitations();
    loadNotificationSettings(); // Phase 2
    loadCompanySettings(); // Load logo
    loadBackupInfo(); // Load backup info
  }, []);

  // Load system logs when tab is active
  useEffect(() => {
    if (activeTab === 'logs-system') {
      loadSystemLogs();
    }
  }, [activeTab, logsCurrentPage, logsPerPage]); // eslint-disable-line

  const loadSmtpConfig = async () => {
    try {
      const config = await apiRequest('smtp/config');
      setSmtpConfig(config);
    } catch (error) {
      // If no config exists, use defaults
      console.log('No SMTP config found, using defaults');
    }
  };

  const loadUserSignature = async () => {
    try {
      const signature = await apiRequest('user/signature');
      setUserSignature(signature);
    } catch (error) {
      // If no signature exists, use defaults
      console.log('No user signature found, using defaults');
    }
  };

  const loadTestLogs = async () => {
    try {
      const logs = await apiRequest('smtp/test-logs');
      setTestLogs(logs);
    } catch (error) {
      console.log('No test logs found');
    }
  };

  const loadRoles = async () => {
    try {
      const rolesData = await apiRequest('roles');
      setRoles(rolesData);
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };

  const loadPermissions = async () => {
    try {
      const permissionsData = await apiRequest('permissions');
      setPermissions(permissionsData);
      
      const modulesData = await apiRequest('permissions/modules');
      setPermissionModules(modulesData.modules);
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const usersData = await apiRequest('admin/users');
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadInvitations = async () => {
    try {
      const invitationsData = await apiRequest('invitations');
      setInvitations(invitationsData);
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  };

  // Phase 2 - Load notification settings
  const loadNotificationSettings = async () => {
    try {
      const settings = await apiRequest('notification-settings');
      setNotificationSettings(settings);
    } catch (error) {
      console.log('No notification settings found, using defaults');
    }
  };

  // Phase 2 - Save notification settings
  const handleSaveNotificationSettings = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await apiRequest('notification-settings', 'PUT', notificationSettings);
      setSuccess('Paramètres de notification sauvegardés avec succès');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSMTPConfig = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    // Store previous config for logging
    const previousConfig = { ...smtpConfig };
    
    try {
      // Try to update first, if fails then create
      try {
        await apiRequest('smtp/config', 'PUT', smtpConfig);
        setSuccess('Configuration SMTP mise à jour avec succès');
        
        // Log SMTP configuration update
        if (user) {
          const changedFields = Object.keys(smtpConfig).filter(key => 
            previousConfig[key] !== smtpConfig[key]
          );
          await ActivityLogger.settingsUpdated(user, 'smtp_config', previousConfig, smtpConfig, changedFields);
        }
      } catch (updateError) {
        // If update fails, try to create
        await apiRequest('smtp/config', 'POST', smtpConfig);
        setSuccess('Configuration SMTP créée avec succès');
        
        // Log SMTP configuration creation
        if (user) {
          await ActivityLogger.settingsUpdated(user, 'smtp_config', null, smtpConfig, Object.keys(smtpConfig));
        }
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTestSMTPConnection = async () => {
    if (!testEmail) {
      setError('Veuillez saisir un email de test');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await apiRequest('smtp/test', 'POST', {
        test_email: testEmail,
        subject: 'Test de configuration SMTP - Baobab Connect',
        message: 'Ceci est un test de configuration SMTP depuis Baobab Connect.'
      });
      setSuccess('Test SMTP réussi ! Vérifiez votre boîte email.');
      loadTestLogs(); // Reload logs after test
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // LLM Configuration functions
  const loadLlmConfigurations = async () => {
    try {
      const configs = await apiRequest('llm-configurations');
      setLlmConfigurations(configs);
    } catch (error) {
      console.error('Error loading LLM configurations:', error);
    }
  };

  const handleSaveLlmConfig = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await apiRequest('llm-configurations', 'POST', llmConfig);
      setSuccess('Configuration LLM créée avec succès');
      loadLlmConfigurations();
      // Reset form
      setLlmConfig({
        provider: 'openai',
        model_name: 'gpt-4',
        api_key: '',
        api_base_url: '',
        temperature: 0.7,
        max_tokens: 1000,
        is_active: true,
        is_default: false
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTestLlmConnection = async () => {
    if (!llmConfig.api_key) {
      setError('Veuillez saisir la clé API');
      return;
    }
    
    setTestingLlm(true);
    setError(null);
    setLlmTestResult(null);
    
    try {
      const testRequest = {
        provider: llmConfig.provider,
        model_name: llmConfig.model_name,
        api_key: llmConfig.api_key,
        api_base_url: llmConfig.api_base_url || null,
        test_prompt: "Bonjour, ceci est un test de connexion. Veuillez répondre avec un simple message de confirmation."
      };
      
      const result = await apiRequest('llm-configurations/test', 'POST', testRequest);
      setLlmTestResult(result);
      
      if (result.success) {
        setSuccess('Test de connexion réussi !');
      } else {
        setError(`Test échoué: ${result.error}`);
      }
    } catch (error) {
      setError(error.message);
      setLlmTestResult({
        success: false,
        error: error.message
      });
    } finally {
      setTestingLlm(false);
    }
  };

  const handleDeleteLlmConfig = async (configId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette configuration ?')) {
      return;
    }
    
    try {
      await apiRequest(`llm-configurations/${configId}`, 'DELETE');
      setSuccess('Configuration supprimée avec succès');
      loadLlmConfigurations();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleToggleLlmConfig = async (configId, isActive) => {
    try {
      await apiRequest(`llm-configurations/${configId}`, 'PUT', { is_active: !isActive });
      setSuccess('Configuration mise à jour avec succès');
      loadLlmConfigurations();
    } catch (error) {
      setError(error.message);
    }
  };

  // Get model options based on provider
  const getModelOptions = (provider) => {
    switch (provider) {
      case 'openai':
        return [
          { value: 'gpt-4', label: 'GPT-4' },
          { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
          { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' }
        ];
      case 'anthropic':
        return [
          { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
          { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
          { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' }
        ];
      case 'google':
        return [
          { value: 'gemini-pro', label: 'Gemini Pro' },
          { value: 'gemini-pro-vision', label: 'Gemini Pro Vision' }
        ];
      default:
        return [];
    }
  };

  const handleSaveUserSignature = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    // Store previous signature for logging
    const previousSignature = { ...userSignature };
    
    try {
      // Try to update first, if fails then create
      try {
        await apiRequest('user/signature', 'PUT', userSignature);
        setSuccess('Signature utilisateur mise à jour avec succès');
        
        // Log signature update activity
        if (user) {
          await ActivityLogger.signatureUpdated(user, userSignature, previousSignature);
        }
      } catch (updateError) {
        // If update fails, try to create
        await apiRequest('user/signature', 'POST', userSignature);
        setSuccess('Signature utilisateur créée avec succès');
        
        // Log signature creation activity
        if (user) {
          await ActivityLogger.signatureCreated(user, userSignature);
        }
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    if (!newRole.name || !newRole.description) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await apiRequest('roles', 'POST', newRole);
      setSuccess('Rôle créé avec succès');
      setShowCreateRoleModal(false);
      setNewRole({ name: '', description: '', permissions: [] });
      loadRoles();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (roleId, roleData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await apiRequest(`roles/${roleId}`, 'PUT', roleData);
      setSuccess('Rôle mis à jour avec succès');
      loadRoles();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce rôle ?')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await apiRequest(`roles/${roleId}`, 'DELETE');
      setSuccess('Rôle supprimé avec succès');
      loadRoles();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!newInvitation.email || !newInvitation.role_id) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await apiRequest('invitations', 'POST', newInvitation);
      setSuccess('Invitation envoyée avec succès');
      setShowInviteUserModal(false);
      setNewInvitation({ email: '', role_id: '', send_email: true });
      loadInvitations();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette invitation ?')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await apiRequest(`invitations/${invitationId}`, 'DELETE');
      setSuccess('Invitation annulée avec succès');
      loadInvitations();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserRole = async (userId, roleId, roleName) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await apiRequest(`users/${userId}/role`, 'PUT', { role_id: roleId, role_name: roleName });
      setSuccess('Rôle utilisateur mis à jour avec succès');
      loadUsers();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Logo upload functions
  const loadCompanySettings = async () => {
    try {
      const settings = await apiRequest('settings/company');
      setCurrentLogo(settings.logo_url);
    } catch (error) {
      console.log('No company settings found, using defaults');
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Le fichier doit être une image');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('La taille du fichier ne doit pas dépasser 2MB');
      return;
    }

    // Validate file extension
    const allowedExtensions = ['.png', '.jpg', '.jpeg'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      setError('Seuls les fichiers PNG, JPG et JPEG sont autorisés');
      return;
    }

    setLogoFile(file);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    setLogoUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('logo_file', logoFile);

      const token = getAuthToken();
      const response = await fetch(`${backendUrl}/api/settings/logo/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
        throw new Error(errorData.detail || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      setSuccess('Logo mis à jour avec succès');
      setCurrentLogo(result.logo_url);
      setLogoPreview(null);
      setLogoFile(null);
      
      // Reset file input
      const fileInput = document.querySelector('input[type="file"][accept*="image"]');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleCancelLogoUpload = () => {
    setLogoPreview(null);
    setLogoFile(null);
    setError(null);
    
    // Reset file input
    const fileInput = document.querySelector('input[type="file"][accept*="image"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Backup & Restore functions
  const loadBackupInfo = async () => {
    try {
      const info = await apiRequest('backup/info');
      setBackupInfo(info);
    } catch (error) {
      console.error('Error loading backup info:', error);
    }
  };

  const handleExportBackup = async () => {
    setBackupGenerating(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = getAuthToken();
      
      // Create AbortController for timeout management
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 300000); // 5 minutes timeout for backup operations
      
      const response = await fetch(`${backendUrl}/api/backup/export`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.status === 401 || response.status === 403) {
        throw new Error('Session expirée. Veuillez vous reconnecter et réessayer.');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
        throw new Error(errorData.detail || `Erreur HTTP: ${response.status}`);
      }

      // Get filename from response headers or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `baobab_backup_${new Date().toISOString().slice(0, 19).replace(/[:]/g, '-')}.json`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) {
          filename = match[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess(`Backup exporté avec succès : ${filename}`);
      
      // Reload backup info to get updated stats
      loadBackupInfo();
      
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('Opération de backup annulée - timeout dépassé. Veuillez réessayer. Si le problème persiste, contactez l\'administrateur.');
      } else {
        setError(error.message);
      }
    } finally {
      setBackupGenerating(false);
    }
  };

  const handleRestoreFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.json')) {
      setError('Le fichier doit être au format JSON');
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      setError('La taille du fichier ne doit pas dépasser 50MB');
      return;
    }

    setRestoreFile(file);
    setError(null);
    setRestoreResult(null);
  };

  const handleImportBackup = async () => {
    if (!restoreFile) {
      setError('Veuillez sélectionner un fichier de backup');
      return;
    }

    setRestoring(true);
    setError(null);
    setSuccess(null);
    setRestoreResult(null);

    try {
      const formData = new FormData();
      formData.append('backup_file', restoreFile);
      formData.append('overwrite_existing', restoreOptions.overwrite_existing);
      formData.append('skip_users', restoreOptions.skip_users);
      formData.append('skip_system_data', restoreOptions.skip_system_data);

      const token = getAuthToken();
      
      // Create AbortController for timeout management
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 600000); // 10 minutes timeout for restore operations (longer than export)
      
      const response = await fetch(`${backendUrl}/api/backup/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.status === 401 || response.status === 403) {
        throw new Error('Session expirée. Veuillez vous reconnecter et réessayer la restauration.');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
        throw new Error(errorData.detail || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      setRestoreResult(result);
      
      if (result.success) {
        setSuccess(`${result.message} (${result.total_documents_restored} documents restaurés)`);
        // Clear the file after successful restore
        setRestoreFile(null);
        const fileInput = document.querySelector('input[type="file"][accept=".json"]');
        if (fileInput) {
          fileInput.value = '';
        }
        // Reload backup info to get updated stats
        loadBackupInfo();
      } else {
        setError(`Restauration partiellement réussie: ${result.message}`);
      }
      
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('Opération de restauration annulée - timeout dépassé. La restauration peut prendre du temps pour de gros fichiers. Veuillez réessayer ou contactez l\'administrateur.');
      } else {
        setError(error.message);
      }
    } finally {
      setRestoring(false);
    }
  };

  const handleRestoreBackup = async () => {
    return handleImportBackup();
  };

  // System Logs functions
  const loadSystemLogs = async () => {
    setLogsLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams();
      
      if (logsFilters.user_id) queryParams.append('user_id', logsFilters.user_id);
      if (logsFilters.action_type) queryParams.append('action_type', logsFilters.action_type);
      if (logsFilters.document_type) queryParams.append('document_type', logsFilters.document_type);
      if (logsFilters.start_date) queryParams.append('start_date', logsFilters.start_date);
      if (logsFilters.end_date) queryParams.append('end_date', logsFilters.end_date);
      if (logsFilters.is_internal !== '') queryParams.append('is_internal', logsFilters.is_internal);
      
      queryParams.append('limit', logsPerPage);
      queryParams.append('offset', (logsCurrentPage - 1) * logsPerPage);
      
      const result = await apiRequest(`activity-logs?${queryParams.toString()}`);
      setSystemLogs(result.logs || []);
      setLogsTotalCount(result.total || 0);
    } catch (error) {
      setError(error.message);
      setSystemLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleLogsFilterChange = (filterName, value) => {
    setLogsFilters(prev => ({ ...prev, [filterName]: value }));
    setLogsCurrentPage(1); // Reset to first page when filters change
  };

  const handleLogsSearch = () => {
    loadSystemLogs();
  };

  const handleLogsClearFilters = () => {
    setLogsFilters({
      user_id: '',
      action_type: '',
      document_type: '',
      start_date: '',
      end_date: '',
      is_internal: '',
      limit: 100,
      offset: 0
    });
    setLogsCurrentPage(1);
    setTimeout(() => loadSystemLogs(), 100);
  };

  const handleLogsPageChange = (newPage) => {
    setLogsCurrentPage(newPage);
    setTimeout(() => loadSystemLogs(), 100);
  };

  const handleExportLogsCSV = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const queryParams = new URLSearchParams();
      
      if (logsFilters.user_id) queryParams.append('user_id', logsFilters.user_id);
      if (logsFilters.action_type) queryParams.append('action_type', logsFilters.action_type);
      if (logsFilters.document_type) queryParams.append('document_type', logsFilters.document_type);
      if (logsFilters.start_date) queryParams.append('start_date', logsFilters.start_date);
      if (logsFilters.end_date) queryParams.append('end_date', logsFilters.end_date);
      
      const token = getAuthToken();
      const response = await fetch(`${backendUrl}/api/activity-logs/export/csv?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
        throw new Error(errorData.detail || `Erreur HTTP: ${response.status}`);
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `logs_systeme_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess('Logs exportés avec succès en CSV');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportLogsPDF = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const queryParams = new URLSearchParams();
      
      if (logsFilters.user_id) queryParams.append('user_id', logsFilters.user_id);
      if (logsFilters.action_type) queryParams.append('action_type', logsFilters.action_type);
      if (logsFilters.document_type) queryParams.append('document_type', logsFilters.document_type);
      if (logsFilters.start_date) queryParams.append('start_date', logsFilters.start_date);
      if (logsFilters.end_date) queryParams.append('end_date', logsFilters.end_date);
      
      const token = getAuthToken();
      const response = await fetch(`${backendUrl}/api/activity-logs/export/pdf?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
        throw new Error(errorData.detail || `Erreur HTTP: ${response.status}`);
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `logs_systeme_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess('Logs exportés avec succès en PDF');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRestore = () => {
    setRestoreFile(null);
    setRestoreResult(null);
    setError(null);
    
    // Reset file input
    const fileInput = document.querySelector('input[type="file"][accept=".json"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // ====================================
  // SCHEDULED BACKUP FUNCTIONS
  // ====================================

  const loadScheduledBackupConfig = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${backendUrl}/api/scheduled-backups/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const config = await response.json();
        setScheduledBackupConfig(config);
      } else {
        console.log('No scheduled backup config found, using defaults');
      }
    } catch (error) {
      console.error('Error loading scheduled backup config:', error);
    }
  };

  const saveScheduledBackupConfig = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = getAuthToken();
      const response = await fetch(`${backendUrl}/api/scheduled-backups/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(scheduledBackupConfig)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
        throw new Error(errorData.detail || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      setSuccess(result.message || 'Configuration de sauvegarde programmée sauvegardée avec succès');
      
      // Reload the config to get the updated version
      await loadScheduledBackupConfig();

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadBackupHistory = async () => {
    setBackupHistoryLoading(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${backendUrl}/api/backup/history?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        setBackupHistory(result.history || []);
      } else {
        throw new Error('Erreur lors du chargement de l\'historique');
      }
    } catch (error) {
      console.error('Error loading backup history:', error);
      setError(error.message);
    } finally {
      setBackupHistoryLoading(false);
    }
  };

  const loadBackupStats = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${backendUrl}/api/backup/history/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const stats = await response.json();
        setBackupStats(stats);
      }
    } catch (error) {
      console.error('Error loading backup stats:', error);
    }
  };

  const downloadBackup = async (backupId, filename) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${backendUrl}/api/backup/download/${backupId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess(`Backup téléchargé: ${filename}`);
    } catch (error) {
      setError(error.message);
    }
  };

  const deleteBackup = async (backupId, filename) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le backup "${filename}" ?`)) {
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch(`${backendUrl}/api/backup/${backupId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }

      setSuccess(`Backup supprimé: ${filename}`);
      await loadBackupHistory(); // Reload history
      await loadBackupStats(); // Reload stats
    } catch (error) {
      setError(error.message);
    }
  };

  const restoreFromBackup = async (backupId, filename) => {
    if (!confirm(`Êtes-vous sûr de vouloir restaurer depuis "${filename}" ?\n\nCette opération peut écraser les données existantes.`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = getAuthToken();
      const response = await fetch(`${backendUrl}/api/backup/restore/${backupId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(restoreOptions)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
        throw new Error(errorData.detail || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      setRestoreResult(result);
      setSuccess(`Restauration réussie depuis: ${filename}`);

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const createManualBackup = async () => {
    setBackupGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      const token = getAuthToken();
      const response = await fetch(`${backendUrl}/api/backup/manual`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
        throw new Error(errorData.detail || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      setSuccess(result.message);
      
      // Reload history and stats
      await loadBackupHistory();
      await loadBackupStats();

    } catch (error) {
      setError(error.message);
    } finally {
      setBackupGenerating(false);
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { color: 'bg-green-100 text-green-800', label: 'Réussi' },
      failed: { color: 'bg-red-100 text-red-800', label: 'Échec' },
      running: { color: 'bg-blue-100 text-blue-800', label: 'En cours' }
    };

    const config = statusConfig[status] || statusConfig.completed;
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getBackupTypeBadge = (type) => {
    const typeConfig = {
      scheduled: { color: 'bg-blue-100 text-blue-800', label: 'Programmé', icon: '⏰' },
      manual: { color: 'bg-purple-100 text-purple-800', label: 'Manuel', icon: '👤' }
    };

    const config = typeConfig[type] || typeConfig.manual;
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${config.color} flex items-center`}>
        <span className="mr-1">{config.icon}</span>
        {config.label}
      </span>
    );
  };

  // ====================================
  // END SCHEDULED BACKUP FUNCTIONS
  // ====================================

  // ====================================
  // EXTERNAL BACKUP SERVERS FUNCTIONS
  // ====================================

  const loadExternalServers = async () => {
    setExternalServersLoading(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${backendUrl}/api/external-backup-servers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        setExternalServers(result.servers || []);
      } else {
        throw new Error('Erreur lors du chargement des serveurs');
      }
    } catch (error) {
      console.error('Error loading external servers:', error);
      setError(error.message);
    } finally {
      setExternalServersLoading(false);
    }
  };

  const loadExternalUploads = async () => {
    setExternalUploadsLoading(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${backendUrl}/api/external-backup-uploads?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        setExternalUploads(result.uploads || []);
      } else {
        throw new Error('Erreur lors du chargement de l\'historique');
      }
    } catch (error) {
      console.error('Error loading external uploads:', error);
      setError(error.message);
    } finally {
      setExternalUploadsLoading(false);
    }
  };

  const saveExternalServer = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = getAuthToken();
      const url = editingServer 
        ? `${backendUrl}/api/external-backup-servers/${editingServer.id}`
        : `${backendUrl}/api/external-backup-servers`;
      
      const method = editingServer ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(serverFormData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
        throw new Error(errorData.detail || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      setSuccess(result.message || 'Serveur sauvegardé avec succès');
      
      // Reload servers and close form
      await loadExternalServers();
      setShowServerForm(false);
      setEditingServer(null);
      resetServerForm();

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteExternalServer = async (serverId, serverName) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le serveur "${serverName}" ?`)) {
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch(`${backendUrl}/api/external-backup-servers/${serverId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }

      setSuccess(`Serveur ${serverName} supprimé avec succès`);
      await loadExternalServers();
    } catch (error) {
      setError(error.message);
    }
  };

  const testServerConnection = async (serverId, serverName) => {
    setTestingServer(serverId);
    setServerTestResult(null);
    setError(null);

    try {
      const token = getAuthToken();
      const response = await fetch(`${backendUrl}/api/external-backup-servers/${serverId}/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Erreur lors du test de connexion');
      }

      const result = await response.json();
      setServerTestResult(result.test_result);
      
      if (result.test_result.success) {
        setSuccess(`Test de connexion réussi pour ${serverName}`);
      } else {
        setError(`Test de connexion échoué pour ${serverName}: ${result.test_result.message}`);
      }

    } catch (error) {
      setError(error.message);
    } finally {
      setTestingServer(null);
    }
  };

  const editExternalServer = (server) => {
    setEditingServer(server);
    setServerFormData({
      name: server.name || '',
      protocol: server.protocol || 'ssh',
      host: server.host || '',
      port: server.port || 22,
      username: server.username || '',
      auth_type: server.auth_type || 'password',
      password: '', // Ne pas pré-remplir pour sécurité
      ssh_private_key: '', // Ne pas pré-remplir pour sécurité
      ssh_key_passphrase: '', // Ne pas pré-remplir pour sécurité
      remote_directory: server.remote_directory || '/backups/baobab',
      is_enabled: server.is_enabled !== undefined ? server.is_enabled : true,
      auto_upload: server.auto_upload !== undefined ? server.auto_upload : true,
      keep_local_after_upload: server.keep_local_after_upload !== undefined ? server.keep_local_after_upload : true,
      max_retries: server.max_retries || 3,
      retry_delay_seconds: server.retry_delay_seconds || 30
    });
    setShowServerForm(true);
  };

  const resetServerForm = () => {
    setServerFormData({
      name: '',
      protocol: 'ssh',
      host: '',
      port: 22,
      username: '',
      auth_type: 'password',
      password: '',
      ssh_private_key: '',
      ssh_key_passphrase: '',
      remote_directory: '/backups/baobab',
      is_enabled: true,
      auto_upload: true,
      keep_local_after_upload: true,
      max_retries: 3,
      retry_delay_seconds: 30
    });
    setEditingServer(null);
    setServerTestResult(null);
  };

  const getProtocolBadge = (protocol) => {
    const protocolConfig = {
      ssh: { color: 'bg-blue-100 text-blue-800', label: 'SSH' },
      sftp: { color: 'bg-green-100 text-green-800', label: 'SFTP' },
      ftp: { color: 'bg-orange-100 text-orange-800', label: 'FTP' }
    };

    const config = protocolConfig[protocol] || protocolConfig.ssh;
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getUploadStatusBadge = (status) => {
    const statusConfig = {
      completed: { color: 'bg-green-100 text-green-800', label: 'Terminé', icon: '✅' },
      failed: { color: 'bg-red-100 text-red-800', label: 'Échec', icon: '❌' },
      uploading: { color: 'bg-blue-100 text-blue-800', label: 'En cours', icon: '⏳' },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'En attente', icon: '⏸️' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${config.color} flex items-center`}>
        <span className="mr-1">{config.icon}</span>
        {config.label}
      </span>
    );
  };

  const format_file_size = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // ====================================
  // END EXTERNAL BACKUP SERVERS FUNCTIONS
  // ====================================

  const mainTabs = [
    { 
      id: 'templates-email', 
      label: 'Templates & Email', 
      icon: '📧',
      subTabs: [
        { id: 'templates', label: 'Templates Email', icon: '📝' },
        { id: 'smtp', label: 'Paramétrage SMTP', icon: '⚙️' },
        { id: 'signatures', label: 'Signatures utilisateurs', icon: '✍️' },
        { id: 'tests', label: 'Tests d\'envoi', icon: '🧪' }
      ]
    },
    { id: 'cgt-templates', label: 'Templates CGT', icon: '📄' },
    { id: 'login-branding', label: 'Page de connexion', icon: '🎨' },
    { id: 'ai-integration', label: 'AI Integration', icon: '🤖' },
    { id: 'users', label: 'Utilisateurs', icon: '👤' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'access-roles', label: 'Accès & Rôles', icon: '🔐' },
    { id: 'backup-restore', label: 'Backup & Restore', icon: '💾' },
    { id: 'monitoring', label: 'Monitoring Système', icon: '🎯' },
    { id: 'logs-system', label: 'Logs système', icon: '📋' },
    { id: 'general', label: 'Configuration générale', icon: '🔧' },
    { id: 'logo', label: 'Logo', icon: '🖼️' },
    { id: 'nomenclature', label: 'Nomenclature', icon: '🔢' }
  ];

  const renderTemplatesEmailContent = () => {
    switch (activeEmailSubTab) {
      case 'templates':
        return <EmailTemplatesManager />;
      
      case 'smtp':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800">Paramétrage SMTP</h3>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-700">{success}</p>
              </div>
            )}
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6">
                <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleSaveSMTPConfig(); }}>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom d'utilisateur SMTP
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                        value={smtpConfig.smtp_username}
                        onChange={(e) => setSmtpConfig({...smtpConfig, smtp_username: e.target.value})}
                        placeholder="Dev-crm@baobaco.tech"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mot de passe SMTP
                      </label>
                      <input
                        type="password"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                        value={smtpConfig.smtp_password}
                        onChange={(e) => setSmtpConfig({...smtpConfig, smtp_password: e.target.value})}
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Serveur SMTP
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                        value={smtpConfig.smtp_server}
                        onChange={(e) => setSmtpConfig({...smtpConfig, smtp_server: e.target.value})}
                        placeholder="smtp.mail.ovh.net"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Port
                      </label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                        value={smtpConfig.smtp_port}
                        onChange={(e) => setSmtpConfig({...smtpConfig, smtp_port: parseInt(e.target.value)})}
                        placeholder="465"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sécurité
                      </label>
                      <select 
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                        value={smtpConfig.security_type}
                        onChange={(e) => setSmtpConfig({...smtpConfig, security_type: e.target.value})}
                      >
                        <option value="SSL/TLS">SSL/TLS</option>
                        <option value="STARTTLS">STARTTLS</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom d'expéditeur
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                        value={smtpConfig.sender_name}
                        onChange={(e) => setSmtpConfig({...smtpConfig, sender_name: e.target.value})}
                        placeholder="Baobab Connect"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email expéditeur
                    </label>
                    <input
                      type="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      value={smtpConfig.sender_email}
                      onChange={(e) => setSmtpConfig({...smtpConfig, sender_email: e.target.value})}
                      placeholder="Dev-crm@baobaco.tech"
                    />
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    {/* Champ email de test */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email de test
                      </label>
                      <input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="votre-email@exemple.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Un email de test sera envoyé à cette adresse pour vérifier la configuration
                      </p>
                    </div>
                    
                    <div className="flex justify-between">
                      <button
                        type="button"
                        onClick={handleTestSMTPConnection}
                        disabled={loading}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        {loading ? '⏳ Test...' : '🧪 Tester la connexion'}
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="bg-baobab-primary text-white px-6 py-2 rounded-lg hover:bg-baobab-dark transition-colors disabled:opacity-50"
                      >
                        {loading ? '⏳ Sauvegarde...' : 'Enregistrer'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        );
      
      case 'signatures':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800">Signatures utilisateurs</h3>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-700">{success}</p>
              </div>
            )}
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Signature par défaut
                    </label>
                    <textarea
                      rows="8"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      value={userSignature.signature_text}
                      onChange={(e) => setUserSignature({...userSignature, signature_text: e.target.value})}
                      placeholder={`Cordialement,

{nom_utilisateur}
{fonction_utilisateur}
{email_utilisateur}
{telephone_utilisateur}

Baobab Connect
ERP / CRM Interne`}
                    />
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-2">Variables disponibles :</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div>• {`{{nom_utilisateur}}`}</div>
                      <div>• {`{{fonction_utilisateur}}`}</div>
                      <div>• {`{{email_utilisateur}}`}</div>
                      <div>• {`{{telephone_utilisateur}}`}</div>
                      <div>• {`{{societe}}`}</div>
                      <div>• {`{{site_web}}`}</div>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <button 
                      onClick={handleSaveUserSignature}
                      disabled={loading}
                      className="bg-baobab-primary text-white px-6 py-2 rounded-lg hover:bg-baobab-dark transition-colors disabled:opacity-50"
                    >
                      {loading ? '⏳ Sauvegarde...' : 'Enregistrer la signature'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'tests':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800">Tests d'envoi</h3>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-700">{success}</p>
              </div>
            )}
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email de test
                    </label>
                    <input
                      type="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="test@example.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Template à tester
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary">
                      <option>Test de configuration</option>
                      <option>Devis</option>
                      <option>Bon de commande</option>
                      <option>Facture</option>
                      <option>Relance</option>
                    </select>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <button 
                      onClick={handleTestSMTPConnection}
                      disabled={loading}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? '⏳ Envoi...' : '📧 Envoyer test'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h4 className="font-medium text-gray-700">Historique des tests</h4>
              </div>
              <div className="p-4">
                <div className="space-y-3">
                  {testLogs.length > 0 ? (
                    testLogs.map((test, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <span className="text-sm text-gray-600">
                            {new Date(test.tested_at).toLocaleString('fr-FR')}
                          </span>
                          <span className="text-sm text-gray-700">{test.test_email}</span>
                          <span className="text-sm text-gray-600">{test.subject}</span>
                        </div>
                        <span className={`text-sm px-2 py-1 rounded ${
                          test.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {test.status === 'success' ? 'Succès' : 'Échec'}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      Aucun test effectué
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  const renderMainContent = () => {
    switch (activeTab) {
      case 'templates-email':
        return renderTemplatesEmailContent();
      
      case 'cgt-templates':
        return <CGTTemplatesManager />;
      
      case 'login-branding':
        return <LoginBrandingManager />;
      
      case 'ai-integration':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">Configuration AI Integration</h3>
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-700">{success}</p>
              </div>
            )}
            
            {/* Add New LLM Configuration */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h4 className="font-medium text-gray-700">Ajouter une nouvelle configuration LLM</h4>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fournisseur LLM
                      </label>
                      <select
                        value={llmConfig.provider}
                        onChange={(e) => {
                          const provider = e.target.value;
                          const defaultModel = getModelOptions(provider)[0]?.value || '';
                          setLlmConfig({
                            ...llmConfig,
                            provider: provider,
                            model_name: defaultModel
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      >
                        <option value="openai">OpenAI</option>
                        <option value="anthropic">Anthropic Claude</option>
                        <option value="google">Google Gemini</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Modèle
                      </label>
                      <select
                        value={llmConfig.model_name}
                        onChange={(e) => setLlmConfig({...llmConfig, model_name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      >
                        {getModelOptions(llmConfig.provider).map(model => (
                          <option key={model.value} value={model.value}>
                            {model.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Clé API
                    </label>
                    <input
                      type="password"
                      value={llmConfig.api_key}
                      onChange={(e) => setLlmConfig({...llmConfig, api_key: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      placeholder="Entrez votre clé API"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      URL de base (optionnel)
                    </label>
                    <input
                      type="url"
                      value={llmConfig.api_base_url}
                      onChange={(e) => setLlmConfig({...llmConfig, api_base_url: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      placeholder="https://api.openai.com/v1"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Température ({llmConfig.temperature})
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={llmConfig.temperature}
                        onChange={(e) => setLlmConfig({...llmConfig, temperature: parseFloat(e.target.value)})}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tokens max
                      </label>
                      <input
                        type="number"
                        value={llmConfig.max_tokens}
                        onChange={(e) => setLlmConfig({...llmConfig, max_tokens: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                        min="100"
                        max="4000"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={llmConfig.is_active}
                        onChange={(e) => setLlmConfig({...llmConfig, is_active: e.target.checked})}
                        className="rounded border-gray-300 text-baobab-primary focus:ring-baobab-primary"
                      />
                      <span className="text-sm text-gray-700">Configuration active</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={llmConfig.is_default}
                        onChange={(e) => setLlmConfig({...llmConfig, is_default: e.target.checked})}
                        className="rounded border-gray-300 text-baobab-primary focus:ring-baobab-primary"
                      />
                      <span className="text-sm text-gray-700">Configuration par défaut</span>
                    </label>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex justify-between">
                      <button
                        type="button"
                        onClick={handleTestLlmConnection}
                        disabled={testingLlm || !llmConfig.api_key}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        {testingLlm ? '⏳ Test...' : '🧪 Tester la connexion'}
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveLlmConfig}
                        disabled={loading || !llmConfig.api_key}
                        className="bg-baobab-primary text-white px-6 py-2 rounded-lg hover:bg-baobab-dark transition-colors disabled:opacity-50"
                      >
                        {loading ? '⏳ Sauvegarde...' : 'Enregistrer'}
                      </button>
                    </div>
                  </div>
                  
                  {/* Test Results */}
                  {llmTestResult && (
                    <div className={`mt-4 p-4 rounded-lg border ${
                      llmTestResult.success 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-red-50 border-red-200'
                    }`}>
                      <h5 className="font-medium text-gray-700 mb-2">Résultat du test</h5>
                      {llmTestResult.success ? (
                        <div className="space-y-2">
                          <p className="text-green-700">✅ Connexion réussie</p>
                          <p className="text-sm text-gray-600">
                            <strong>Temps de réponse:</strong> {llmTestResult.response_time?.toFixed(2)}s
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Réponse:</strong> {llmTestResult.response}
                          </p>
                        </div>
                      ) : (
                        <p className="text-red-700">❌ {llmTestResult.error}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Existing LLM Configurations */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h4 className="font-medium text-gray-700">Configurations LLM existantes</h4>
              </div>
              <div className="p-4">
                <div className="space-y-3">
                  {llmConfigurations.length > 0 ? (
                    llmConfigurations.map((config) => (
                      <div key={config.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-2xl">
                              {config.provider === 'openai' && '🤖'}
                              {config.provider === 'anthropic' && '🧠'}
                              {config.provider === 'google' && '🔍'}
                            </span>
                            <div>
                              <span className="font-medium text-gray-700 capitalize">
                                {config.provider}
                              </span>
                              <span className="text-sm text-gray-500 ml-2">
                                ({config.model_name})
                              </span>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            {config.is_default && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                Par défaut
                              </span>
                            )}
                            <span className={`text-xs px-2 py-1 rounded ${
                              config.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {config.is_active ? 'Actif' : 'Inactif'}
                            </span>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleToggleLlmConfig(config.id, config.is_active)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            {config.is_active ? 'Désactiver' : 'Activer'}
                          </button>
                          <button
                            onClick={() => handleDeleteLlmConfig(config.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>Aucune configuration LLM trouvée</p>
                      <p className="text-sm">Ajoutez une configuration ci-dessus pour commencer</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'users':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">Gestion des utilisateurs</h3>
              <button 
                onClick={() => setShowInviteUserModal(true)}
                className="bg-baobab-primary text-white px-4 py-2 rounded-lg hover:bg-baobab-dark transition-colors"
              >
                + Inviter un utilisateur
              </button>
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-700">{success}</p>
              </div>
            )}
            
            {/* Active Users */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h4 className="font-medium text-gray-700">Utilisateurs actifs</h4>
              </div>
              <div className="p-4">
                {users.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2">Nom complet</th>
                          <th className="text-left py-2">Email</th>
                          <th className="text-left py-2">Rôle</th>
                          <th className="text-left py-2">Statut</th>
                          <th className="text-left py-2">Dernière connexion</th>
                          <th className="text-left py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr key={user.id} className="border-b border-gray-100">
                            <td className="py-3">{user.full_name}</td>
                            <td className="py-3">{user.email}</td>
                            <td className="py-3">
                              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                                {user.role_name || user.role}
                              </span>
                            </td>
                            <td className="py-3">
                              <span className={`px-2 py-1 rounded text-xs ${
                                user.is_active 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {user.is_active ? 'Actif' : 'Inactif'}
                              </span>
                            </td>
                            <td className="py-3">
                              {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : '-'}
                            </td>
                            <td className="py-3">
                              <div className="flex space-x-2">
                                <select
                                  value={user.role_id || ''}
                                  onChange={(e) => {
                                    const selectedRole = roles.find(r => r.id === e.target.value);
                                    if (selectedRole) {
                                      handleUpdateUserRole(user.id, selectedRole.id, selectedRole.name);
                                    }
                                  }}
                                  className="text-xs border border-gray-300 rounded px-2 py-1"
                                >
                                  <option value="">Changer le rôle</option>
                                  {roles.map((role) => (
                                    <option key={role.id} value={role.id}>
                                      {role.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Aucun utilisateur trouvé
                  </div>
                )}
              </div>
            </div>

            {/* Pending Invitations */}
            {invitations.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h4 className="font-medium text-gray-700">Invitations en attente</h4>
                </div>
                <div className="p-4">
                  <div className="space-y-3">
                    {invitations.filter(inv => inv.status === 'pending').map((invitation) => (
                      <div key={invitation.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <span className="text-sm text-gray-700">{invitation.email}</span>
                          <span className="text-sm text-gray-600">Rôle: {invitation.role_name}</span>
                          <span className="text-xs text-gray-500">
                            Expire le: {new Date(invitation.expires_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        <button
                          onClick={() => handleCancelInvitation(invitation.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Annuler
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Invite User Modal */}
            {showInviteUserModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                  <h3 className="text-lg font-semibold mb-4">Inviter un utilisateur</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={newInvitation.email}
                        onChange={(e) => setNewInvitation({...newInvitation, email: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                        placeholder="utilisateur@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rôle
                      </label>
                      <select
                        value={newInvitation.role_id}
                        onChange={(e) => setNewInvitation({...newInvitation, role_id: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      >
                        <option value="">Sélectionner un rôle</option>
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name} - {role.description}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="send_email"
                        checked={newInvitation.send_email}
                        onChange={(e) => setNewInvitation({...newInvitation, send_email: e.target.checked})}
                        className="mr-2"
                      />
                      <label htmlFor="send_email" className="text-sm text-gray-700">
                        Envoyer l'invitation par email
                      </label>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={() => setShowInviteUserModal(false)}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleInviteUser}
                      disabled={loading}
                      className="px-4 py-2 bg-baobab-primary text-white rounded-lg hover:bg-baobab-dark disabled:opacity-50"
                    >
                      {loading ? 'Envoi...' : 'Envoyer l\'invitation'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'notifications':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800">Notifications</h3>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-700">Notifications par email</h4>
                      <p className="text-sm text-gray-600">Recevoir des notifications par email</p>
                    </div>
                    <input type="checkbox" className="w-4 h-4 text-baobab-primary" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-700">Notifications push</h4>
                      <p className="text-sm text-gray-600">Recevoir des notifications push</p>
                    </div>
                    <input type="checkbox" className="w-4 h-4 text-baobab-primary" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-700">Relances automatiques</h4>
                      <p className="text-sm text-gray-600">Activer les relances automatiques</p>
                    </div>
                    <input type="checkbox" className="w-4 h-4 text-baobab-primary" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'access-roles':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">Accès & Rôles</h3>
              <button 
                onClick={() => setShowCreateRoleModal(true)}
                className="bg-baobab-primary text-white px-4 py-2 rounded-lg hover:bg-baobab-dark transition-colors"
              >
                + Nouveau rôle
              </button>
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-700">{success}</p>
              </div>
            )}
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {roles.map((role) => (
                    <div key={role.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium text-gray-700">{role.name}</h4>
                        {role.is_system_role && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            Système
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{role.description}</p>
                      <div className="text-xs text-gray-500 mb-3">
                        {role.permissions?.length || 0} permissions
                      </div>
                      
                      {/* Permissions by module */}
                      <div className="space-y-2 mb-4">
                        {permissionModules.map((module) => {
                          const modulePermissions = role.permission_details?.filter(p => p.module === module) || [];
                          if (modulePermissions.length === 0) return null;
                          
                          return (
                            <div key={module} className="text-xs">
                              <span className="font-medium text-gray-600 capitalize">{module}:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {modulePermissions.map((perm) => (
                                  <span key={perm.id} className="bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                    {perm.name.replace(`${module}_`, '').replace('_', ' ')}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {!role.is_system_role && (
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => {
                              setEditingRole(role);
                              setNewRole({
                                name: role.name,
                                description: role.description,
                                permissions: role.permissions || []
                              });
                              setShowCreateRoleModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            Modifier
                          </button>
                          <button 
                            onClick={() => handleDeleteRole(role.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Supprimer
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Create/Edit Role Modal */}
            {showCreateRoleModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-96 overflow-y-auto">
                  <h3 className="text-lg font-semibold mb-4">
                    {editingRole ? 'Modifier le rôle' : 'Créer un nouveau rôle'}
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nom du rôle
                        </label>
                        <input
                          type="text"
                          value={newRole.name}
                          onChange={(e) => setNewRole({...newRole, name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                          placeholder="Ex: Responsable Logistique"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <input
                          type="text"
                          value={newRole.description}
                          onChange={(e) => setNewRole({...newRole, description: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                          placeholder="Description du rôle"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Permissions
                      </label>
                      <div className="border border-gray-300 rounded-md p-4 max-h-48 overflow-y-auto">
                        {permissionModules.map((module) => {
                          const modulePermissions = permissions.filter(p => p.module === module);
                          
                          return (
                            <div key={module} className="mb-4">
                              <h4 className="font-medium text-gray-700 capitalize mb-2">{module}</h4>
                              <div className="grid grid-cols-2 gap-2">
                                {modulePermissions.map((permission) => (
                                  <label key={permission.id} className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      checked={newRole.permissions.includes(permission.id)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setNewRole({
                                            ...newRole,
                                            permissions: [...newRole.permissions, permission.id]
                                          });
                                        } else {
                                          setNewRole({
                                            ...newRole,
                                            permissions: newRole.permissions.filter(p => p !== permission.id)
                                          });
                                        }
                                      }}
                                      className="text-baobab-primary"
                                    />
                                    <span className="text-sm text-gray-700">{permission.description}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={() => {
                        setShowCreateRoleModal(false);
                        setEditingRole(null);
                        setNewRole({ name: '', description: '', permissions: [] });
                      }}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={() => {
                        if (editingRole) {
                          handleUpdateRole(editingRole.id, newRole);
                        } else {
                          handleCreateRole();
                        }
                        setEditingRole(null);
                      }}
                      disabled={loading}
                      className="px-4 py-2 bg-baobab-primary text-white rounded-lg hover:bg-baobab-dark disabled:opacity-50"
                    >
                      {loading ? 'Sauvegarde...' : (editingRole ? 'Mettre à jour' : 'Créer')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'notifications':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800">Préférences de Notifications</h3>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-700">{success}</p>
              </div>
            )}
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h4 className="font-medium text-gray-700">Paramètres généraux</h4>
              </div>
              <div className="p-6">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Notifications par email
                      </label>
                      <p className="text-sm text-gray-500 mt-1">
                        Recevoir des notifications par email lorsque vous êtes mentionné ou assigné
                      </p>
                    </div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={notificationSettings.email_notifications}
                        onChange={(e) => setNotificationSettings({
                          ...notificationSettings,
                          email_notifications: e.target.checked
                        })}
                        className="rounded border-gray-300 text-baobab-primary focus:ring-baobab-primary"
                      />
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Notifications dans l'application
                      </label>
                      <p className="text-sm text-gray-500 mt-1">
                        Afficher les notifications dans l'interface de l'application
                      </p>
                    </div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={notificationSettings.in_app_notifications}
                        onChange={(e) => setNotificationSettings({
                          ...notificationSettings,
                          in_app_notifications: e.target.checked
                        })}
                        className="rounded border-gray-300 text-baobab-primary focus:ring-baobab-primary"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h4 className="font-medium text-gray-700">Types de notifications</h4>
                <p className="text-sm text-gray-500 mt-1">
                  Choisissez les types de notifications que vous souhaitez recevoir
                </p>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {[
                    {
                      key: 'user_tag',
                      label: 'Mentions utilisateur (@nom)',
                      description: 'Quand quelqu\'un vous mentionne dans un commentaire',
                      icon: '👤'
                    },
                    {
                      key: 'comment_reply',
                      label: 'Réponses aux commentaires',
                      description: 'Quand quelqu\'un répond à vos commentaires',
                      icon: '💬'
                    },
                    {
                      key: 'document_update',
                      label: 'Mises à jour de documents',
                      description: 'Quand un document que vous suivez est modifié',
                      icon: '📄'
                    },
                    {
                      key: 'assignment',
                      label: 'Assignations',
                      description: 'Quand vous êtes assigné à une tâche ou un document',
                      icon: '📋'
                    },
                    {
                      key: 'status_change',
                      label: 'Changements de statut',
                      description: 'Quand le statut d\'un élément que vous suivez change',
                      icon: '🔄'
                    },
                    {
                      key: 'system',
                      label: 'Notifications système',
                      description: 'Notifications importantes du système',
                      icon: '⚙️'
                    }
                  ].map((notifType) => (
                    <div key={notifType.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <span className="text-xl">{notifType.icon}</span>
                        <div>
                          <label className="text-sm font-medium text-gray-700">
                            {notifType.label}
                          </label>
                          <p className="text-sm text-gray-500">
                            {notifType.description}
                          </p>
                        </div>
                      </div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={notificationSettings.notification_types[notifType.key] || false}
                          onChange={(e) => setNotificationSettings({
                            ...notificationSettings,
                            notification_types: {
                              ...notificationSettings.notification_types,
                              [notifType.key]: e.target.checked
                            }
                          })}
                          className="rounded border-gray-300 text-baobab-primary focus:ring-baobab-primary"
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={handleSaveNotificationSettings}
                disabled={loading}
                className="bg-baobab-primary text-white px-6 py-2 rounded-lg hover:bg-baobab-dark transition-colors disabled:opacity-50"
              >
                {loading ? '⏳ Sauvegarde...' : 'Enregistrer les préférences'}
              </button>
            </div>
          </div>
        );
      
      case 'backup-restore':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">Backup & Restore</h3>
              <div className="text-sm text-gray-600">
                {backupStats && (
                  <span>Total: {backupStats.total_backups} sauvegardes ({backupStats.total_size_human})</span>
                )}
              </div>
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-700">{success}</p>
              </div>
            )}

            {/* Backup Tabs */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6">
                  {[
                    { id: 'manual', label: 'Backup Manuel', icon: '👤' },
                    { id: 'scheduled', label: 'Sauvegarde Programmée', icon: '⏰' },
                    { id: 'history', label: 'Historique', icon: '📋' },
                    { id: 'external', label: 'Serveurs Externes', icon: '🌐' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveBackupTab(tab.id);
                        if (tab.id === 'history') {
                          loadBackupHistory();
                          loadBackupStats();
                        } else if (tab.id === 'scheduled') {
                          loadScheduledBackupConfig();
                        } else if (tab.id === 'external') {
                          loadExternalServers();
                          loadExternalUploads();
                        }
                      }}
                      className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                        activeBackupTab === tab.id
                          ? 'border-baobab-primary text-baobab-primary'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span className="mr-2">{tab.icon}</span>
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {activeBackupTab === 'manual' && (
                  <div className="space-y-6">
                    {/* Manual Backup Section */}
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-700 flex items-center">
                        <span className="mr-2">📦</span>
                        Backup Manuel
                      </h4>
                      <p className="text-sm text-gray-600">
                        Créez une sauvegarde immédiate de toutes les données de l'application.
                      </p>
                      
                      {/* Backup Info Display */}
                      {backupInfo && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h5 className="font-medium text-gray-700 mb-3">Données disponibles pour export :</h5>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {Object.entries(backupInfo.collections).map(([collection, count]) => (
                              <div key={collection} className="text-sm">
                                <span className="font-medium text-gray-700 capitalize">
                                  {collection.replace('_', ' ')}:
                                </span>
                                <span className="ml-2 text-gray-600">{count} documents</span>
                              </div>
                            ))}
                          </div>
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-sm font-medium text-gray-700">
                              Total: {backupInfo.total_documents} documents dans {Object.keys(backupInfo.collections).length} collections
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex space-x-4">
                        <button
                          onClick={handleExportBackup}
                          disabled={backupGenerating}
                          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
                        >
                          {backupGenerating ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Génération en cours...
                            </>
                          ) : (
                            <>
                              <span className="mr-2">⬇️</span>
                              Télécharger Backup JSON
                            </>
                          )}
                        </button>
                        
                        <button
                          onClick={createManualBackup}
                          disabled={backupGenerating}
                          className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center"
                        >
                          {backupGenerating ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Création...
                            </>
                          ) : (
                            <>
                              <span className="mr-2">💾</span>
                              Créer Backup Compressé
                            </>
                          )}
                        </button>
                      </div>
                      
                      {backupGenerating && (
                        <div className="mt-3 text-sm text-blue-600">
                          <p>⏳ L'export peut prendre quelques minutes pour de gros volumes de données...</p>
                          <p className="text-xs text-gray-500 mt-1">Votre session reste active pendant l'opération</p>
                        </div>
                      )}
                    </div>

                    {/* Manual Restore Section */}
                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="font-medium text-gray-700 flex items-center mb-4">
                        <span className="mr-2">📥</span>
                        Restaurer Backup
                      </h4>
                      
                      <div className="space-y-4">
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <div className="flex items-start">
                            <span className="text-yellow-600 mr-2"></span>
                            <div className="text-sm text-yellow-800">
                              <strong>Attention:</strong> La restauration peut écraser les données existantes. 
                              Il est recommandé de faire un backup avant de procéder à une restauration.
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Fichier de backup (JSON, GZIP, ZIP)
                          </label>
                          <input
                            type="file"
                            accept=".json,.gz,.zip"
                            onChange={handleRestoreFileChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                          />
                          {restoreFile && (
                            <div className="mt-2 text-sm text-gray-600">
                              Fichier sélectionné: {restoreFile.name} ({(restoreFile.size / 1024 / 1024).toFixed(2)} MB)
                            </div>
                          )}
                        </div>
                        
                        {/* Restore Options */}
                        <div className="space-y-3">
                          <h5 className="font-medium text-gray-700">Options de restauration :</h5>
                          
                          <label className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={restoreOptions.overwrite_existing}
                              onChange={(e) => setRestoreOptions({
                                ...restoreOptions,
                                overwrite_existing: e.target.checked
                              })}
                              className="rounded border-gray-300 text-baobab-primary focus:ring-baobab-primary"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-700">Écraser les données existantes</span>
                              <p className="text-xs text-gray-500">Remplace les documents existants par ceux du backup</p>
                            </div>
                          </label>
                          
                          <label className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={restoreOptions.skip_users}
                              onChange={(e) => setRestoreOptions({
                                ...restoreOptions,
                                skip_users: e.target.checked
                              })}
                              className="rounded border-gray-300 text-baobab-primary focus:ring-baobab-primary"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-700">Ignorer les utilisateurs</span>
                              <p className="text-xs text-gray-500">Ne pas restaurer les comptes utilisateurs</p>
                            </div>
                          </label>
                          
                          <label className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={restoreOptions.skip_system_data}
                              onChange={(e) => setRestoreOptions({
                                ...restoreOptions,
                                skip_system_data: e.target.checked
                              })}
                              className="rounded border-gray-300 text-baobab-primary focus:ring-baobab-primary"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-700">Ignorer les données système</span>
                              <p className="text-xs text-gray-500">Ne pas restaurer les rôles et permissions</p>
                            </div>
                          </label>
                        </div>
                        
                        <div className="pt-4 border-t border-gray-200">
                          <div className="flex justify-between">
                            <button
                              onClick={handleCancelRestore}
                              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                              disabled={restoring}
                            >
                              Annuler
                            </button>
                            <button
                              onClick={handleRestoreBackup}
                              disabled={!restoreFile || restoring}
                              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center"
                            >
                              {restoring ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                  Restauration...
                                </>
                              ) : (
                                <>
                                  <span className="mr-2">🔄</span>
                                  Restaurer les données
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Restore Result */}
                    {restoreResult && (
                      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h5 className="font-medium text-blue-900 mb-2">Résultat de la restauration</h5>
                        <div className="text-sm text-blue-800">
                          <p><strong>Status:</strong> {restoreResult.success ? 'Succès' : 'Échec'}</p>
                          <p><strong>Message:</strong> {restoreResult.message}</p>
                          {restoreResult.collections_restored && Object.keys(restoreResult.collections_restored).length > 0 && (
                            <div className="mt-2">
                              <p><strong>Collections restaurées:</strong></p>
                              <ul className="list-disc list-inside ml-4">
                                {Object.entries(restoreResult.collections_restored).map(([collection, count]) => (
                                  <li key={collection}>{collection}: {count} documents</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeBackupTab === 'scheduled' && (
                  <div className="space-y-6">
                    <h4 className="font-medium text-gray-700 flex items-center">
                      <span className="mr-2">⏰</span>
                      Configuration des Sauvegardes Programmées
                    </h4>

                    <div className="space-y-4">
                      {/* Enable/Disable */}
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={scheduledBackupConfig.is_enabled}
                          onChange={(e) => setScheduledBackupConfig({
                            ...scheduledBackupConfig,
                            is_enabled: e.target.checked
                          })}
                          className="rounded border-gray-300 text-baobab-primary focus:ring-baobab-primary"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-700">Activer les sauvegardes programmées</span>
                          <p className="text-xs text-gray-500">Créer automatiquement des sauvegardes selon la fréquence configurée</p>
                        </div>
                      </label>

                      {scheduledBackupConfig.is_enabled && (
                        <>
                          {/* Frequency */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Fréquence
                              </label>
                              <select
                                value={scheduledBackupConfig.frequency}
                                onChange={(e) => setScheduledBackupConfig({
                                  ...scheduledBackupConfig,
                                  frequency: e.target.value
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                              >
                                <option value="daily">Quotidien</option>
                                <option value="weekly">Hebdomadaire</option>
                                <option value="monthly">Mensuel</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Heure d'exécution
                              </label>
                              <div className="flex space-x-2">
                                <input
                                  type="number"
                                  min="0"
                                  max="23"
                                  value={scheduledBackupConfig.hour}
                                  onChange={(e) => setScheduledBackupConfig({
                                    ...scheduledBackupConfig,
                                    hour: parseInt(e.target.value)
                                  })}
                                  className="w-16 px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                                />
                                <span className="py-2">:</span>
                                <input
                                  type="number"
                                  min="0"
                                  max="59"
                                  value={scheduledBackupConfig.minute}
                                  onChange={(e) => setScheduledBackupConfig({
                                    ...scheduledBackupConfig,
                                    minute: parseInt(e.target.value)
                                  })}
                                  className="w-16 px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Additional frequency options */}
                          {scheduledBackupConfig.frequency === 'weekly' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Jour de la semaine
                              </label>
                              <select
                                value={scheduledBackupConfig.day_of_week || 0}
                                onChange={(e) => setScheduledBackupConfig({
                                  ...scheduledBackupConfig,
                                  day_of_week: parseInt(e.target.value)
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                              >
                                <option value={0}>Lundi</option>
                                <option value={1}>Mardi</option>
                                <option value={2}>Mercredi</option>
                                <option value={3}>Jeudi</option>
                                <option value={4}>Vendredi</option>
                                <option value={5}>Samedi</option>
                                <option value={6}>Dimanche</option>
                              </select>
                            </div>
                          )}

                          {scheduledBackupConfig.frequency === 'monthly' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Jour du mois
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="31"
                                value={scheduledBackupConfig.day_of_month || 1}
                                onChange={(e) => setScheduledBackupConfig({
                                  ...scheduledBackupConfig,
                                  day_of_month: parseInt(e.target.value)
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                              />
                            </div>
                          )}

                          {/* Compression Settings */}
                          <div className="space-y-3">
                            <h5 className="font-medium text-gray-700">Paramètres de compression :</h5>
                            
                            <label className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={scheduledBackupConfig.compression_enabled}
                                onChange={(e) => setScheduledBackupConfig({
                                  ...scheduledBackupConfig,
                                  compression_enabled: e.target.checked
                                })}
                                className="rounded border-gray-300 text-baobab-primary focus:ring-baobab-primary"
                              />
                              <span className="text-sm font-medium text-gray-700">Activer la compression</span>
                            </label>

                            {scheduledBackupConfig.compression_enabled && (
                              <div className="ml-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Format de compression
                                </label>
                                <select
                                  value={scheduledBackupConfig.compression_format}
                                  onChange={(e) => setScheduledBackupConfig({
                                    ...scheduledBackupConfig,
                                    compression_format: e.target.value
                                  })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                                >
                                  <option value="gzip">GZIP (recommandé)</option>
                                  <option value="zip">ZIP</option>
                                </select>
                              </div>
                            )}
                          </div>

                          {/* Retention Settings */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Rétention (jours)
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="365"
                                value={scheduledBackupConfig.retention_days}
                                onChange={(e) => setScheduledBackupConfig({
                                  ...scheduledBackupConfig,
                                  retention_days: parseInt(e.target.value)
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                              />
                              <p className="text-xs text-gray-500 mt-1">Suppression automatique après X jours</p>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Nombre max de sauvegardes
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="100"
                                value={scheduledBackupConfig.max_backups}
                                onChange={(e) => setScheduledBackupConfig({
                                  ...scheduledBackupConfig,
                                  max_backups: parseInt(e.target.value)
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                              />
                              <p className="text-xs text-gray-500 mt-1">Conserve seulement les X dernières sauvegardes</p>
                            </div>
                          </div>
                        </>
                      )}

                      {/* Save Configuration */}
                      <div className="pt-4 border-t border-gray-200">
                        <button
                          onClick={saveScheduledBackupConfig}
                          disabled={loading}
                          className="bg-baobab-primary text-white px-6 py-2 rounded-lg hover:bg-baobab-dark transition-colors disabled:opacity-50"
                        >
                          {loading ? '⏳ Sauvegarde...' : 'Enregistrer la configuration'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeBackupTab === 'history' && (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium text-gray-700 flex items-center">
                        <span className="mr-2">📋</span>
                        Historique des Sauvegardes
                      </h4>
                      <button
                        onClick={() => {
                          loadBackupHistory();
                          loadBackupStats();
                        }}
                        disabled={backupHistoryLoading}
                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                      >
                        <span className="mr-1">🔄</span>
                        Actualiser
                      </button>
                    </div>

                    {/* Statistics */}
                    {backupStats && (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-blue-700">{backupStats.total_backups}</div>
                          <div className="text-sm text-blue-600">Total Sauvegardes</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-green-700">{backupStats.successful_backups}</div>
                          <div className="text-sm text-green-600">Réussies</div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-red-700">{backupStats.failed_backups}</div>
                          <div className="text-sm text-red-600">Échecs</div>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-purple-700">{backupStats.total_size_human}</div>
                          <div className="text-sm text-purple-600">Taille Totale</div>
                        </div>
                      </div>
                    )}

                    {/* Backup History Table */}
                    {backupHistoryLoading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-baobab-primary mx-auto"></div>
                        <p className="text-gray-600 mt-2">Chargement de l'historique...</p>
                      </div>
                    ) : backupHistory.length > 0 ? (
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Nom</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Type</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Taille</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Collections</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Statut</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Date</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {backupHistory.map((backup) => (
                                <tr key={backup.id} className="hover:bg-gray-50">
                                  <td className="py-3 px-4">
                                    <div className="flex items-center">
                                      <span className="font-medium text-gray-900">{backup.filename}</span>
                                      {backup.is_compressed && (
                                        <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                          {backup.compression_format?.toUpperCase()}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="py-3 px-4">
                                    {getBackupTypeBadge(backup.backup_type)}
                                  </td>
                                  <td className="py-3 px-4 text-gray-600">
                                    {backup.file_size_human}
                                  </td>
                                  <td className="py-3 px-4 text-gray-600">
                                    {backup.collections_count} collections
                                    <br />
                                    <span className="text-xs text-gray-500">{backup.total_documents} documents</span>
                                  </td>
                                  <td className="py-3 px-4">
                                    {getStatusBadge(backup.status)}
                                  </td>
                                  <td className="py-3 px-4 text-gray-600">
                                    {formatDateTime(backup.created_at)}
                                    {backup.execution_time_seconds && (
                                      <>
                                        <br />
                                        <span className="text-xs text-gray-500">
                                          ({backup.execution_time_seconds.toFixed(1)}s)
                                        </span>
                                      </>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    <div className="flex space-x-2">
                                      {backup.status === 'completed' && (
                                        <>
                                          <button
                                            onClick={() => downloadBackup(backup.id, backup.filename)}
                                            className="text-blue-600 hover:text-blue-800 text-xs"
                                            title="Télécharger"
                                          >
                                            ⬇️
                                          </button>
                                          <button
                                            onClick={() => restoreFromBackup(backup.id, backup.filename)}
                                            className="text-green-600 hover:text-green-800 text-xs"
                                            title="Restaurer"
                                          >
                                            🔄
                                          </button>
                                        </>
                                      )}
                                      <button
                                        onClick={() => deleteBackup(backup.id, backup.filename)}
                                        className="text-red-600 hover:text-red-800 text-xs"
                                        title="Supprimer"
                                      >
                                        🗑️
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <p>Aucune sauvegarde trouvée</p>
                        <p className="text-sm mt-1">Les sauvegardes créées apparaîtront ici</p>
                      </div>
                    )}
                  </div>
                )}

                {activeBackupTab === 'external' && (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium text-gray-700 flex items-center">
                        <span className="mr-2">🌐</span>
                        Serveurs de Sauvegarde Externe
                      </h4>
                      <button
                        onClick={() => {
                          resetServerForm();
                          setShowServerForm(true);
                        }}
                        className="bg-baobab-primary text-white px-4 py-2 rounded-lg hover:bg-baobab-dark transition-colors text-sm flex items-center"
                      >
                        <span className="mr-2">➕</span>
                        Ajouter Serveur
                      </button>
                    </div>

                    {/* Server Form Modal */}
                    {showServerForm && (
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex justify-between items-center mb-4">
                          <h5 className="font-medium text-gray-700">
                            {editingServer ? 'Modifier le serveur' : 'Nouveau serveur externe'}
                          </h5>
                          <button
                            onClick={() => {
                              setShowServerForm(false);
                              resetServerForm();
                            }}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            ✕
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Basic Configuration */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Nom du serveur *
                            </label>
                            <input
                              type="text"
                              value={serverFormData.name}
                              onChange={(e) => setServerFormData({...serverFormData, name: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                              placeholder="Ex: Serveur Production SSH"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Protocole *
                            </label>
                            <select
                              value={serverFormData.protocol}
                              onChange={(e) => setServerFormData({
                                ...serverFormData, 
                                protocol: e.target.value,
                                port: e.target.value === 'ftp' ? 21 : 22
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                            >
                              <option value="ssh">SSH</option>
                              <option value="sftp">SFTP</option>
                              <option value="ftp">FTP</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Adresse serveur *
                            </label>
                            <input
                              type="text"
                              value={serverFormData.host}
                              onChange={(e) => setServerFormData({...serverFormData, host: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                              placeholder="Ex: backup.monentreprise.com"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Port
                            </label>
                            <input
                              type="number"
                              value={serverFormData.port}
                              onChange={(e) => setServerFormData({...serverFormData, port: parseInt(e.target.value)})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Nom d'utilisateur *
                            </label>
                            <input
                              type="text"
                              value={serverFormData.username}
                              onChange={(e) => setServerFormData({...serverFormData, username: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                              placeholder="Ex: backup_user"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Répertoire distant
                            </label>
                            <input
                              type="text"
                              value={serverFormData.remote_directory}
                              onChange={(e) => setServerFormData({...serverFormData, remote_directory: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                              placeholder="/backups/baobab"
                            />
                          </div>
                        </div>

                        {/* Authentication */}
                        <div className="mt-6">
                          <h6 className="font-medium text-gray-700 mb-3">Authentification</h6>
                          
                          {serverFormData.protocol !== 'ftp' && (
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Type d'authentification
                              </label>
                              <select
                                value={serverFormData.auth_type}
                                onChange={(e) => setServerFormData({...serverFormData, auth_type: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                              >
                                <option value="password">Mot de passe</option>
                                <option value="ssh_key">Clé SSH privée</option>
                              </select>
                            </div>
                          )}

                          {(serverFormData.auth_type === 'password' || serverFormData.protocol === 'ftp') && (
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Mot de passe
                              </label>
                              <input
                                type="password"
                                value={serverFormData.password}
                                onChange={(e) => setServerFormData({...serverFormData, password: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                                placeholder={editingServer ? "Laisser vide pour conserver" : "Mot de passe"}
                              />
                            </div>
                          )}

                          {serverFormData.auth_type === 'ssh_key' && serverFormData.protocol !== 'ftp' && (
                            <>
                              <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Clé privée SSH
                                </label>
                                <textarea
                                  value={serverFormData.ssh_private_key}
                                  onChange={(e) => setServerFormData({...serverFormData, ssh_private_key: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                                  rows="4"
                                  placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                                />
                              </div>

                              <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Phrase de passe de la clé (optionnel)
                                </label>
                                <input
                                  type="password"
                                  value={serverFormData.ssh_key_passphrase}
                                  onChange={(e) => setServerFormData({...serverFormData, ssh_key_passphrase: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                                  placeholder="Phrase de passe pour la clé SSH"
                                />
                              </div>
                            </>
                          )}
                        </div>

                        {/* Options */}
                        <div className="mt-6">
                          <h6 className="font-medium text-gray-700 mb-3">Options</h6>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={serverFormData.is_enabled}
                                onChange={(e) => setServerFormData({...serverFormData, is_enabled: e.target.checked})}
                                className="rounded border-gray-300 text-baobab-primary focus:ring-baobab-primary"
                              />
                              <span className="text-sm font-medium text-gray-700">Serveur activé</span>
                            </label>

                            <label className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={serverFormData.auto_upload}
                                onChange={(e) => setServerFormData({...serverFormData, auto_upload: e.target.checked})}
                                className="rounded border-gray-300 text-baobab-primary focus:ring-baobab-primary"
                              />
                              <span className="text-sm font-medium text-gray-700">Upload automatique</span>
                            </label>

                            <label className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={serverFormData.keep_local_after_upload}
                                onChange={(e) => setServerFormData({...serverFormData, keep_local_after_upload: e.target.checked})}
                                className="rounded border-gray-300 text-baobab-primary focus:ring-baobab-primary"
                              />
                              <span className="text-sm font-medium text-gray-700">Conserver local après upload</span>
                            </label>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mt-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Nombre max de tentatives
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={serverFormData.max_retries}
                                onChange={(e) => setServerFormData({...serverFormData, max_retries: parseInt(e.target.value)})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Délai entre tentatives (sec)
                              </label>
                              <input
                                type="number"
                                min="5"
                                max="300"
                                value={serverFormData.retry_delay_seconds}
                                onChange={(e) => setServerFormData({...serverFormData, retry_delay_seconds: parseInt(e.target.value)})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between">
                          <button
                            onClick={() => {
                              setShowServerForm(false);
                              resetServerForm();
                            }}
                            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                            disabled={loading}
                          >
                            Annuler
                          </button>
                          <button
                            onClick={saveExternalServer}
                            disabled={loading || !serverFormData.name || !serverFormData.host || !serverFormData.username}
                            className="bg-baobab-primary text-white px-6 py-2 rounded-lg hover:bg-baobab-dark transition-colors disabled:opacity-50"
                          >
                            {loading ? '⏳ Sauvegarde...' : (editingServer ? 'Mettre à jour' : 'Créer le serveur')}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Servers List */}
                    {externalServersLoading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-baobab-primary mx-auto"></div>
                        <p className="text-gray-600 mt-2">Chargement des serveurs...</p>
                      </div>
                    ) : externalServers.length > 0 ? (
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Nom</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Protocole</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Serveur</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Statut</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Dernier test</th>
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {externalServers.map((server) => (
                                <tr key={server.id} className="hover:bg-gray-50">
                                  <td className="py-3 px-4">
                                    <div className="flex items-center">
                                      <span className="font-medium text-gray-900">{server.name}</span>
                                      {server.auto_upload && (
                                        <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                          Auto
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="py-3 px-4">
                                    {getProtocolBadge(server.protocol)}
                                  </td>
                                  <td className="py-3 px-4 text-gray-600">
                                    {server.host}:{server.port}
                                    <br />
                                    <span className="text-xs text-gray-500">{server.remote_directory}</span>
                                  </td>
                                  <td className="py-3 px-4">
                                    <span className={`px-2 py-1 text-xs rounded-full ${
                                      server.is_enabled 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {server.is_enabled ? 'Actif' : 'Inactif'}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 text-gray-600">
                                    {server.last_test_at ? (
                                      <>
                                        {formatDateTime(server.last_test_at)}
                                        <br />
                                        <span className={`text-xs ${
                                          server.last_test_status === 'success' 
                                            ? 'text-green-600' 
                                            : 'text-red-600'
                                        }`}>
                                          {server.last_test_status === 'success' ? '✅ Réussi' : '❌ Échec'}
                                        </span>
                                      </>
                                    ) : (
                                      <span className="text-xs text-gray-500">Jamais testé</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={() => testServerConnection(server.id, server.name)}
                                        disabled={testingServer === server.id}
                                        className="text-blue-600 hover:text-blue-800 text-xs disabled:opacity-50"
                                        title="Tester la connexion"
                                      >
                                        {testingServer === server.id ? '⏳' : '🔗'}
                                      </button>
                                      <button
                                        onClick={() => editExternalServer(server)}
                                        className="text-green-600 hover:text-green-800 text-xs"
                                        title="Modifier"
                                      >
                                        ✏️
                                      </button>
                                      <button
                                        onClick={() => deleteExternalServer(server.id, server.name)}
                                        className="text-red-600 hover:text-red-800 text-xs"
                                        title="Supprimer"
                                      >
                                        🗑️
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <p>Aucun serveur externe configuré</p>
                        <p className="text-sm mt-1">Cliquez sur "Ajouter Serveur" pour commencer</p>
                      </div>
                    )}

                    {/* External Uploads History */}
                    {externalUploads.length > 0 && (
                      <div className="mt-8">
                        <h5 className="font-medium text-gray-700 mb-4">Historique des uploads externes</h5>
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="text-left py-3 px-4 font-medium text-gray-700">Backup</th>
                                  <th className="text-left py-3 px-4 font-medium text-gray-700">Serveur</th>
                                  <th className="text-left py-3 px-4 font-medium text-gray-700">Taille</th>
                                  <th className="text-left py-3 px-4 font-medium text-gray-700">Statut</th>
                                  <th className="text-left py-3 px-4 font-medium text-gray-700">Date</th>
                                  <th className="text-left py-3 px-4 font-medium text-gray-700">Performance</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {externalUploads.slice(0, 10).map((upload) => (
                                  <tr key={upload.id} className="hover:bg-gray-50">
                                    <td className="py-3 px-4 font-medium text-gray-900">
                                      {upload.backup_filename}
                                    </td>
                                    <td className="py-3 px-4 text-gray-600">
                                      {upload.server_name}
                                    </td>
                                    <td className="py-3 px-4 text-gray-600">
                                      {format_file_size(upload.file_size_bytes)}
                                    </td>
                                    <td className="py-3 px-4">
                                      {getUploadStatusBadge(upload.status)}
                                    </td>
                                    <td className="py-3 px-4 text-gray-600">
                                      {formatDateTime(upload.created_at)}
                                    </td>
                                    <td className="py-3 px-4 text-gray-600">
                                      {upload.upload_duration_seconds && (
                                        <>
                                          {upload.upload_duration_seconds.toFixed(1)}s
                                          {upload.upload_speed_mbps && (
                                            <>
                                              <br />
                                              <span className="text-xs text-gray-500">
                                                {upload.upload_speed_mbps.toFixed(1)} MB/s
                                              </span>
                                            </>
                                          )}
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Test Result Modal */}
                    {serverTestResult && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                          <div className="flex justify-between items-center mb-4">
                            <h5 className="font-medium text-gray-700">Résultat du test de connexion</h5>
                            <button
                              onClick={() => setServerTestResult(null)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              ✕
                            </button>
                          </div>
                          
                          <div className="space-y-3">
                            <div className={`p-3 rounded-lg ${
                              serverTestResult.success 
                                ? 'bg-green-50 border border-green-200' 
                                : 'bg-red-50 border border-red-200'
                            }`}>
                              <p className={`font-medium ${
                                serverTestResult.success ? 'text-green-800' : 'text-red-800'
                              }`}>
                                {serverTestResult.success ? '✅ Test réussi' : '❌ Test échoué'}
                              </p>
                              <p className={`text-sm mt-1 ${
                                serverTestResult.success ? 'text-green-700' : 'text-red-700'
                              }`}>
                                {serverTestResult.message}
                              </p>
                            </div>
                            
                            {serverTestResult.connection_time_ms && (
                              <p className="text-sm text-gray-600">
                                <strong>Temps de connexion:</strong> {serverTestResult.connection_time_ms.toFixed(1)}ms
                              </p>
                            )}
                            
                            {serverTestResult.server_info && (
                              <div className="text-sm text-gray-600">
                                <strong>Informations serveur:</strong>
                                <pre className="mt-1 bg-gray-50 p-2 rounded text-xs">
                                  {JSON.stringify(serverTestResult.server_info, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                          
                          <button
                            onClick={() => setServerTestResult(null)}
                            className="mt-4 w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
                          >
                            Fermer
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      
      case 'monitoring':
        return (
          <div className="monitoring-container">
            <MonitoringDashboard />
          </div>
        );
      
      case 'logs-system':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">Logs système</h3>
              <div className="flex space-x-2">
                <button
                  onClick={handleExportLogsCSV}
                  disabled={loading}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {loading ? '⏳ Export...' : '📊 Exporter CSV'}
                </button>
                <button
                  onClick={handleExportLogsPDF}
                  disabled={loading}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {loading ? '⏳ Export...' : '📄 Exporter PDF'}
                </button>
              </div>
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-700">{success}</p>
              </div>
            )}
            
            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h4 className="font-medium text-gray-700">Filtres</h4>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type d'action
                    </label>
                    <select
                      value={logsFilters.action_type}
                      onChange={(e) => handleLogsFilterChange('action_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                    >
                      <option value="">Toutes les actions</option>
                      <option value="create">Création</option>
                      <option value="update">Modification</option>
                      <option value="delete">Suppression</option>
                      <option value="send">Envoi</option>
                      <option value="status_change">Changement de statut</option>
                      <option value="assign">Assignation</option>
                      <option value="comment">Commentaire</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type de document
                    </label>
                    <select
                      value={logsFilters.document_type}
                      onChange={(e) => handleLogsFilterChange('document_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                    >
                      <option value="">Tous les types</option>
                      <option value="devis">Devis</option>
                      <option value="facture">Facture</option>
                      <option value="bon_commande">Bon de commande</option>
                      <option value="opportunite">Opportunité</option>
                      <option value="operation">Opération</option>
                      <option value="societe">Société</option>
                      <option value="user">Utilisateur</option>
                      <option value="settings">Paramètres</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type de log
                    </label>
                    <select
                      value={logsFilters.is_internal}
                      onChange={(e) => handleLogsFilterChange('is_internal', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                    >
                      <option value="">Tous les logs</option>
                      <option value="false">Actions utilisateur</option>
                      <option value="true">Actions internes</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date de début
                    </label>
                    <input
                      type="datetime-local"
                      value={logsFilters.start_date}
                      onChange={(e) => handleLogsFilterChange('start_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date de fin
                    </label>
                    <input
                      type="datetime-local"
                      value={logsFilters.end_date}
                      onChange={(e) => handleLogsFilterChange('end_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                    />
                  </div>
                </div>
                
                <div className="flex justify-between">
                  <button
                    onClick={handleLogsClearFilters}
                    className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    🗑️ Effacer filtres
                  </button>
                  <button
                    onClick={handleLogsSearch}
                    disabled={logsLoading}
                    className="bg-baobab-primary text-white px-6 py-2 rounded-lg hover:bg-baobab-dark transition-colors disabled:opacity-50"
                  >
                    {logsLoading ? '⏳ Recherche...' : '🔍 Rechercher'}
                  </button>
                </div>
              </div>
            </div>
            
            {/* Logs Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-gray-700">
                    Logs d'activité ({logsTotalCount} total)
                  </h4>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">Logs par page:</span>
                    <select
                      value={logsPerPage}
                      onChange={(e) => {
                        setLogsPerPage(parseInt(e.target.value));
                        setLogsCurrentPage(1);
                        setTimeout(() => loadSystemLogs(), 100);
                      }}
                      className="text-sm border border-gray-300 rounded px-2 py-1"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                {logsLoading ? (
                  <div className="text-center py-8">
                    <div className="baobab-spinner mx-auto mb-4"></div>
                    <p className="text-gray-500">Chargement des logs...</p>
                  </div>
                ) : systemLogs.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-4">Date/Heure</th>
                        <th className="text-left py-3 px-4">Utilisateur</th>
                        <th className="text-left py-3 px-4">Action</th>
                        <th className="text-left py-3 px-4">Module</th>
                        <th className="text-left py-3 px-4">Référence</th>
                        <th className="text-left py-3 px-4">Détails</th>
                        <th className="text-left py-3 px-4">IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {systemLogs.map((log, index) => (
                        <tr key={log.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            {new Date(log.timestamp).toLocaleString('fr-FR')}
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <div className="font-medium text-gray-900">{log.user_name}</div>
                              <div className="text-xs text-gray-500">{log.user_email}</div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              log.action_type === 'create' ? 'bg-green-100 text-green-800' :
                              log.action_type === 'update' ? 'bg-blue-100 text-blue-800' :
                              log.action_type === 'delete' ? 'bg-red-100 text-red-800' :
                              log.action_type === 'send' ? 'bg-purple-100 text-purple-800' :
                              log.action_type === 'status_change' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {log.action_type === 'create' ? 'Création' :
                               log.action_type === 'update' ? 'Modification' :
                               log.action_type === 'delete' ? 'Suppression' :
                               log.action_type === 'send' ? 'Envoi' :
                               log.action_type === 'status_change' ? 'Statut' :
                               log.action_type === 'assign' ? 'Assignation' :
                               log.action_type === 'comment' ? 'Commentaire' :
                               log.action_type}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-gray-700 capitalize">
                              {log.document_type === 'devis' ? 'Devis' :
                               log.document_type === 'facture' ? 'Facture' :
                               log.document_type === 'bon_commande' ? 'Bon de commande' :
                               log.document_type === 'opportunite' ? 'Opportunité' :
                               log.document_type === 'operation' ? 'Opération' :
                               log.document_type === 'societe' ? 'Société' :
                               log.document_type === 'user' ? 'Utilisateur' :
                               log.document_type === 'settings' ? 'Paramètres' :
                               log.document_type}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-gray-600 font-mono text-xs">
                              {log.document_reference || '-'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="max-w-xs">
                              {log.metadata && Object.keys(log.metadata).length > 0 ? (
                                <details className="cursor-pointer">
                                  <summary className="text-blue-600 hover:text-blue-800 text-xs">
                                    Voir détails
                                  </summary>
                                  <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                    <pre className="whitespace-pre-wrap">
                                      {JSON.stringify(log.metadata, null, 2)}
                                    </pre>
                                  </div>
                                </details>
                              ) : (
                                <span className="text-gray-400 text-xs">-</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-gray-500 text-xs font-mono">
                              {log.ip_address || '-'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>Aucun log trouvé avec ces critères</p>
                    <p className="text-sm">Essayez de modifier les filtres ci-dessus</p>
                  </div>
                )}
              </div>
              
              {/* Pagination */}
              {logsTotalCount > logsPerPage && (
                <div className="p-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                      Affichage {((logsCurrentPage - 1) * logsPerPage) + 1} à {Math.min(logsCurrentPage * logsPerPage, logsTotalCount)} sur {logsTotalCount} logs
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleLogsPageChange(logsCurrentPage - 1)}
                        disabled={logsCurrentPage <= 1}
                        className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        ← Précédent
                      </button>
                      
                      <div className="flex space-x-1">
                        {Array.from({ length: Math.min(5, Math.ceil(logsTotalCount / logsPerPage)) }, (_, i) => {
                          const pageNum = Math.max(1, logsCurrentPage - 2) + i;
                          if (pageNum <= Math.ceil(logsTotalCount / logsPerPage)) {
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handleLogsPageChange(pageNum)}
                                className={`px-3 py-1 border rounded text-sm ${
                                  pageNum === logsCurrentPage
                                    ? 'bg-baobab-primary text-white border-baobab-primary'
                                    : 'border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          }
                          return null;
                        })}
                      </div>
                      
                      <button
                        onClick={() => handleLogsPageChange(logsCurrentPage + 1)}
                        disabled={logsCurrentPage >= Math.ceil(logsTotalCount / logsPerPage)}
                        className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Suivant →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      
      case 'general':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800">Configuration générale</h3>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom de l'entreprise
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                      defaultValue="Baobab Connect"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Devise par défaut
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary">
                      <option>FCFA</option>
                      <option>EUR</option>
                      <option>USD</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fuseau horaire
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary">
                      <option>UTC+0 (GMT)</option>
                      <option>UTC+1 (CET)</option>
                      <option>UTC+2 (EET)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'logo':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800">Logo de l'entreprise</h3>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-700">{success}</p>
              </div>
            )}
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Logo actuel
                    </label>
                    <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200">
                      {currentLogo ? (
                        <img 
                          src={currentLogo} 
                          alt="Logo actuel" 
                          className="w-full h-full object-contain rounded-lg"
                        />
                      ) : (
                        <span className="text-gray-400">Aucun logo</span>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nouveau logo
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoFileChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-baobab-primary"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Formats acceptés: PNG, JPG, JPEG. Taille maximale: 2MB
                    </p>
                  </div>
                  
                  {logoPreview && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Aperçu
                      </label>
                      <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200">
                        <img 
                          src={logoPreview} 
                          alt="Aperçu du logo" 
                          className="w-full h-full object-contain rounded-lg"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex gap-3">
                      <button 
                        onClick={handleLogoUpload}
                        disabled={!logoFile || logoUploading}
                        className="bg-baobab-primary text-white px-6 py-2 rounded-lg hover:bg-baobab-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {logoUploading ? '⏳ Téléchargement...' : 'Mettre à jour le logo'}
                      </button>
                      
                      {logoPreview && (
                        <button 
                          onClick={handleCancelLogoUpload}
                          disabled={logoUploading}
                          className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Annuler
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'nomenclature':
        return <NomenclatureTest />;
      
      default:
        return null;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Paramètres</h1>
        <p className="text-gray-600">Gérer les paramètres de l'application</p>
      </div>

      <div className="flex gap-6">
        {/* Sidebar Navigation */}
        <div className="w-64 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4">
            <nav className="space-y-2">
              {mainTabs.map((tab) => (
                <div key={tab.id}>
                  <button
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-baobab-primary text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-lg">{tab.icon}</span>
                    <span className="font-medium">{tab.label}</span>
                  </button>
                  
                  {/* Sub-tabs for Templates & Email */}
                  {tab.id === 'templates-email' && activeTab === 'templates-email' && (
                    <div className="ml-4 mt-2 space-y-1">
                      {tab.subTabs.map((subTab) => (
                        <button
                          key={subTab.id}
                          onClick={() => setActiveEmailSubTab(subTab.id)}
                          className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                            activeEmailSubTab === subTab.id
                              ? 'bg-baobab-light text-baobab-dark'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <span>{subTab.icon}</span>
                          <span>{subTab.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {renderMainContent()}
        </div>
      </div>
    </div>
  );
};

export default SettingsManager;